import { Add, Close, Search } from '@mui/icons-material';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import { TICKET_STATUS_CARD_STYLES, TicketStatus } from 'data/constants/constants';
import useTranslate from 'hooks/useTranslate';
import { getTicketStatusLabelKey } from 'utils/ticket';

const ALL_STATUSES = Object.values(TicketStatus);

interface StatusCount {
    status: TicketStatus;
    count: number;
}

interface TicketToolbarProps {
    search: string;
    onSearchChange: (value: string) => void;
    statusFilter: TicketStatus | null;
    onStatusFilterChange: (status: TicketStatus | null) => void;
    statusCounts: StatusCount[];
    onCreateTicket: () => void;
}

const TicketToolbar = ({
    search,
    onSearchChange,
    statusFilter,
    onStatusFilterChange,
    statusCounts,
    onCreateTicket,
}: TicketToolbarProps) => {
    const { translate } = useTranslate();
    const countMap = new Map(statusCounts.map(({ status, count }) => [status, count]));

    return (
        <Stack spacing={2} sx={{ pt: 2 }}>
            <Stack
                direction="row"
                spacing={2}
                useFlexGap
                sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                }}
            >
                <TextField
                    size="small"
                    placeholder={translate('searchTickets')}
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    sx={{ minWidth: 240, maxWidth: 320, flex: 1 }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            ...(search && {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => onSearchChange('')}
                                            edge="end"
                                            aria-label={translate('clearAll')}
                                        >
                                            <Close sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }),
                        },
                    }}
                />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={onCreateTicket}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    {translate('createTicket')}
                </Button>
            </Stack>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(4, 1fr)',
                    },
                    gap: 1.5,
                }}
            >
                {ALL_STATUSES.map((status) => {
                    const count = countMap.get(status) ?? 0;
                    const style = TICKET_STATUS_CARD_STYLES[status];
                    const isActive = statusFilter === status;

                    return (
                        <Box
                            key={status}
                            onClick={() => onStatusFilterChange(isActive ? null : status)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 1.5,
                                px: 2,
                                borderRadius: 1.5,
                                cursor: 'pointer',
                                bgcolor: isActive ? style.bg : 'grey.50',
                                border: 1,
                                borderColor: isActive ? style.border : 'divider',
                                '&:hover': { bgcolor: style.bg },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 22,
                                    fontWeight: 700,
                                    color: style.color,
                                    lineHeight: 1.2,
                                }}
                            >
                                {count}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: style.color,
                                    mt: 0.25,
                                }}
                            >
                                {translate(getTicketStatusLabelKey(status))}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Stack>
    );
};

export default TicketToolbar;
